import isNaN from 'lodash-es/isNaN'
import { JSX, useMemo } from 'react'
import styled from 'styled-components'

import {
  IconPreviewItem as LMFIconPreviewItem,
  getResponsiveSrcInfo,
} from '@syconium/little-miss-figgy'

import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { IconFormValues } from '../../../../../containers/EmbroideryContainer/types'
import { getFlatlayImageOffset } from '../../../../../containers/EmbroideryContainer/utils/getFlatlayImageOffset'
import {
  EMBROIDERY_ICON_FILTER_ID,
  EMBROIDERY_TEXT_FILTER_ID,
} from '../../../Modal/primitives/EmbroideryThreadFilter'
import { usePreviewFlatlayImage } from '../../EmbroideryModalPreview/PreviewFlatlayImageContext'

export const IconPreviewItem = <T extends Omit<IconFormValues, '__type'>>({
  values,
}: {
  values: T
}) => {
  const { productFlatlayImage } = useEmbroidery()
  const previewFlatlayImage = usePreviewFlatlayImage()
  const backgroundImageOffsets = getFlatlayImageOffset({
    flatlayImage: previewFlatlayImage ?? productFlatlayImage,
    placement: values.iconPlacement,
  })

  const xOffset: string | undefined = backgroundImageOffsets?.x
    ? `${backgroundImageOffsets.x * 100}%`
    : undefined
  const yOffset: string | undefined = backgroundImageOffsets?.y
    ? `${backgroundImageOffsets.y * 100}%`
    : undefined
  const rotation: number | undefined = backgroundImageOffsets?.rotation
  const backgroundImageScale: number = backgroundImageOffsets?.scale ?? 1

  const iconScale = useMemo(() => {
    const scale = values.iconImage?.scale ?? 1
    if (isNaN(scale)) return 1
    return scale
  }, [values])

  const responsiveSource = values.iconImage?.image.src
    ? getResponsiveSrcInfo({
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        src: values.iconImage?.image.src,
        width: 80,
      }).src
    : values.iconImage?.image.src

  const previewItem: JSX.Element | null = useMemo(() => {
    return values.iconPlacement && backgroundImageOffsets ? (
      <IconWrapper>
        <LMFIconPreviewItem
          iconImageName={values.iconImage?.image.label}
          iconImageSource={responsiveSource ?? undefined}
          iconScale={iconScale}
          initials={values.initials}
          initialsColor={values.initialsColor?.hexCode}
          rotation={rotation}
          scale={backgroundImageScale}
          xOffset={xOffset}
          yOffset={yOffset}
        />
      </IconWrapper>
    ) : null
  }, [
    values.iconPlacement,
    backgroundImageOffsets,
    values.iconImage?.image.label,
    values.initials,
    values.initialsColor?.hexCode,
    responsiveSource,
    iconScale,
    rotation,
    backgroundImageScale,
    xOffset,
    yOffset,
  ])

  return previewItem
}

const IconWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: ${o => o.theme.zIndex.foregroundContent};
  --embroidery-icon-filter: url(#${EMBROIDERY_ICON_FILTER_ID});
  --embroidery-initials-filter: url(#${EMBROIDERY_TEXT_FILTER_ID});
  overflow: hidden;
`
